import React, { useEffect, useRef, useState } from "react";
import { TrendingUp, Target, DollarSign, Sparkles } from "lucide-react";

function useAnimatedNumber(value, duration = 200) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const startRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    fromRef.current = display;
    startRef.current = performance.now();
    const animate = (now) => {
      const elapsed = now - startRef.current;
      const t = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = fromRef.current + (value - fromRef.current) * eased;
      setDisplay(current);
      if (t < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration]);

  return display;
}

const fmtMoney = (n) =>
  n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const Slider = ({ label, valueLabel, min, max, step, value, onChange, testId }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <label className="text-xs sm:text-sm font-semibold text-slate-700">{label}</label>
      <span className="text-xs sm:text-sm font-bold text-[#1D6AFF] tabular-nums">{valueLabel}</span>
    </div>
    <input
      data-testid={testId}
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full h-1.5 rounded-full appearance-none cursor-pointer roi-slider"
    />
    <style>{`
      .roi-slider {
        background: linear-gradient(to right, #1D6AFF 0%, #1D6AFF var(--p, 50%), #E2E8F0 var(--p, 50%), #E2E8F0 100%);
      }
      .roi-slider::-webkit-slider-thumb {
        -webkit-appearance: none;
        width: 18px;
        height: 18px;
        border-radius: 9999px;
        background: #fff;
        cursor: pointer;
        box-shadow: 0 2px 8px rgba(29,106,255,0.35), 0 0 0 2px #1D6AFF;
        transition: transform 0.15s ease;
      }
      .roi-slider::-webkit-slider-thumb:hover { transform: scale(1.12); }
      .roi-slider::-moz-range-thumb {
        width: 18px;
        height: 18px;
        border-radius: 9999px;
        background: #fff;
        cursor: pointer;
        border: 2px solid #1D6AFF;
        box-shadow: 0 2px 8px rgba(29,106,255,0.35);
      }
    `}</style>
  </div>
);

const StatCard = ({ icon: Icon, label, value, sublabel, hero, testId }) => (
  <div
    data-testid={testId}
    className={`relative rounded-2xl p-4 sm:p-5 border transition-all ${
      hero
        ? "bg-[#1D6AFF]/8 border-[#1D6AFF]/40 shadow-[0_18px_40px_-15px_rgba(29,106,255,0.4)]"
        : "bg-slate-50 border-slate-200"
    }`}
    style={hero ? { background: "rgba(29,106,255,0.06)" } : undefined}
  >
    <div className="flex items-center gap-2 mb-2">
      <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${hero ? "bg-[#1D6AFF]/15 border border-[#1D6AFF]/30" : "bg-white border border-slate-200"}`}>
        <Icon className="w-3.5 h-3.5 text-[#1D6AFF]" />
      </div>
      <div className="text-[10px] sm:text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</div>
    </div>
    <div className={`font-display font-bold tabular-nums text-slate-900 leading-none mb-1 ${hero ? "text-2xl sm:text-3xl lg:text-4xl" : "text-xl sm:text-2xl"}`}>
      {value}
    </div>
    <div className="text-[10px] sm:text-[11px] text-slate-500 leading-snug">{sublabel}</div>
  </div>
);

export default function ROICalculator() {
  const [budget, setBudget] = useState(900);
  const [salePrice, setSalePrice] = useState(450000);
  const [commission, setCommission] = useState(2.5);

  useEffect(() => {
    document.querySelectorAll(".roi-slider").forEach((el) => {
      const min = Number(el.min);
      const max = Number(el.max);
      const v = Number(el.value);
      const p = ((v - min) / (max - min)) * 100;
      el.style.setProperty("--p", `${p}%`);
    });
  }, [budget, salePrice, commission]);

  const rawLeads = Math.floor(budget / 20);
  const rawClosings = Math.round(rawLeads * 0.02 * 10) / 10;
  const rawCommission = rawClosings * salePrice * (commission / 100);
  const rawAnnual = rawCommission * 12;

  const leads = Math.max(25, rawLeads);
  const closingsNumeric = Math.max(0.5, rawClosings);
  const commissionAmt = Math.max(2250, rawCommission);
  const annualAmt = Math.max(27000, rawAnnual);

  const aLeads = useAnimatedNumber(leads);
  const aClosings = useAnimatedNumber(closingsNumeric);
  const aCommission = useAnimatedNumber(commissionAmt);
  const aAnnual = useAnimatedNumber(annualAmt);

  const closingsDisplay = closingsNumeric < 1 ? "< 1" : aClosings.toFixed(1);

  return (
    <section
      id="roi"
      className="relative py-12 sm:py-20 border-t border-slate-200"
      data-testid="roi-calculator-section"
    >

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-7 sm:mb-10">
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#1D6AFF] mb-3">ROI Calculator</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-3">
            See Your Return Before You Spend a Dime
          </h2>
          <p className="text-sm sm:text-base text-slate-500 max-w-xl mx-auto">
            Move the sliders and watch your potential income update live.
          </p>
        </div>

        <div
          className="relative max-w-[640px] mx-auto rounded-2xl p-5 sm:p-8 bg-white border border-slate-200 shadow-[0_20px_50px_-20px_rgba(15,23,42,0.15)]"
          data-testid="roi-calculator-card"
        >
          <div className="space-y-5 mb-6">
            <Slider
              testId="roi-budget-slider"
              label="Monthly Ad Budget"
              valueLabel={`${fmtMoney(budget)}/month on ads`}
              min={450}
              max={3000}
              step={50}
              value={budget}
              onChange={setBudget}
            />
            <Slider
              testId="roi-saleprice-slider"
              label="Average Home Sale Price"
              valueLabel={`${fmtMoney(salePrice)} avg sale price`}
              min={200000}
              max={2000000}
              step={25000}
              value={salePrice}
              onChange={setSalePrice}
            />
            <Slider
              testId="roi-commission-slider"
              label="Your Commission Rate"
              valueLabel={`${commission.toFixed(1)}% commission`}
              min={1}
              max={5}
              step={0.1}
              value={commission}
              onChange={setCommission}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <StatCard
              testId="roi-card-leads"
              icon={Target}
              label="Estimated Leads/Month"
              value={Math.round(aLeads).toLocaleString()}
              sublabel="Based on $20 avg cost per lead"
            />
            <StatCard
              testId="roi-card-closings"
              icon={TrendingUp}
              label="Projected Closings/Month"
              value={closingsDisplay}
              sublabel="At a conservative 2% closing rate"
            />
            <StatCard
              testId="roi-card-commission"
              icon={DollarSign}
              label="Projected Commission/Month"
              value={fmtMoney(Math.round(aCommission))}
              sublabel="Your estimated monthly earnings"
            />
            <StatCard
              testId="roi-card-annual"
              hero
              icon={Sparkles}
              label="Annual Revenue Potential"
              value={fmtMoney(Math.round(aAnnual))}
              sublabel="Your projected yearly income"
            />
          </div>
        </div>

        <div className="max-w-[640px] mx-auto mt-7 text-center">
          <p className="text-[11px] sm:text-xs text-slate-500 leading-relaxed">
            Based on a conservative 2% closing rate. The average Newground agent close at <span className="text-[#1D6AFF] font-semibold">3.26%</span>, nearly 3× the industry average of 1.2%.
          </p>
        </div>
      </div>
    </section>
  );
}
